/**
 * MOTOR ÚNICO DE INGRESO del ER v2 (Fase 3, en construcción).
 *
 * Reemplaza, módulo a módulo, lo que hoy hacen `usePublicidadCalcs`/`useContratosCalcs`/
 * `useBTLCalcs`/`useHardwareCalcs`/`useGestionCalcs` — cada fuente deja de aplastar
 * directamente a un `Array(12)` y pasa a producir una lista de `RegistroFinancieroER`
 * (ver `registroFinancieroER.js`). Ver docs/decisiones/2026-10-04-reingenieria-ebitda-fase3-diseno.md.
 *
 * PASO 3.2 (este): ventas (`sales`) y contratos (`sales_contratos`) — Bloque A, SOLO ingreso
 * (las 7 comisiones sobre estos mismos registros llegan en el paso 3.4). La conversión
 * LOCAL→US$ de hoy ("último TC del mes") NO se toca acá — eso sigue viviendo en
 * `estadoResultadosER`/los componentes hasta el paso 3.8 (Criterio 1). Lo único que YA es
 * definitivo desde este paso es la conversión MONEDA EXTRANJERA→LOCAL de cada documento (UF/USD
 * de una venta/contrato a CLP/PEN), que es la misma de siempre y no depende del criterio de TC
 * de display.
 *
 * Réplica deliberada (no reimplementación con criterio propio) de:
 *   - `usePublicidadCalcs.js:66-86` (`valoresMensuales`) — filtro `!opp.eliminada && opp.facturafecha`,
 *     SIN filtro de `estatus` (asimetría real con contratos, ver abajo — se preserva, no se corrige).
 *   - `useContratosCalcs.js:53-75` (`calcIngresosContratos`) — filtro `!c.eliminada && c.estatus === "100%"`.
 * Ambos usan `fechaLocalDeNegocio` (H7, `parseLocalYMD`) para ubicar el mes, y la MISMA cascada
 * de conversión extranjera→local (`getRateForOpp`/`getConvertedImporte` para ventas;
 * `c.moneda_dolar ? importe * getDolarValue(fecha) : importe` para contratos — funcionalmente
 * equivalente, los contratos siempre tienen `facturafecha` ya filtrada).
 */

import { calcularImporteNetoIntermediario, getConvertedImporte, getRateForOpp, calculateComission } from "../../utils";
import { getApxMonto } from "../../utils/apx";
import {
  calcularPorcentajeComisionIntermediario,
  obtenerNombreIntermediario,
} from "../../utils/calcularComisionIntermediario";
import { esMesHistorico } from "../../utils/carteleria/corteHistorico";
import {
  registrosGestion as registrosGestionHistorico,
  registrosHardware as registrosHardwareHistorico,
} from "../../utils/carteleria/historico/ingresosERCarteleriaHistorico";
import {
  esNegocioSoloMotor,
  registrosGestionMotor,
  registrosHardwareMotor,
} from "../../utils/carteleria/ingresosERCarteleria";
import {
  calcularComisionEdificiosTotal,
  esEdificioComisionFija,
  resolverFechaFactura,
} from "../../utils/comisionEdificiosUtil";
import { getTakeLocal } from "../../utils/costosIntermediacion";
import { esNotaCredito } from "../../utils/dte";
import { parseLocalYMD } from "../../utils/fechasFuturosPanel";
import { getImporteFacturable } from "../../utils/getImporteFacturable";
import { resolverBloqueLineaCarteleria } from "../../utils/lineasFacturacion/resolverLinea";
import { construirExclusionNcAnulaError } from "../../utils/ncAnulaError";
import { resolverPorcentajeBroker } from "../../utils/resolverPorcentajeBroker";
import { convertirRegistrosAUSD } from "./conversionER";
import { agregarAMesesLocal, agregarAMesesUSD, crearRegistroFinancieroER } from "./registroFinancieroER";

// H7 — idéntico al de usePublicidadCalcs.js/useContratosCalcs.js (mismo wrapper compartido,
// no una copia con otro nombre: 10.243).
const fechaLocalDeNegocio = (fechaStr) => parseLocalYMD(fechaStr) || new Date(NaN);

/**
 * Registros de ingreso de VENTAS (`sales`) de un (pais, ano) — réplica de
 * `usePublicidadCalcs.js:66-86`. Un registro por venta con `facturafecha` real en ese año.
 *
 * @param {object} p
 * @param {Array} p.oportunidades - Filas de `sales` del país (cualquier año; se filtra adentro).
 * @param {"Chile"|"Perú"} p.pais
 * @param {number} p.ano
 * @param {Function} p.getDolarValue - `(fechaStr) => number`, YA bound al país (como hoy).
 * @param {number} p.dolarFallbackPais - Fallback de TC del país (como hoy, `dolarFallback[pais]`).
 * @returns {Array} RegistroFinancieroER[]
 */
export const registrosVentas = ({ oportunidades, pais, ano, getDolarValue, dolarFallbackPais }) => {
  const registros = [];
  (oportunidades || [])
    .filter((opp) => !opp.eliminada && opp.facturafecha)
    .forEach((opp) => {
      const fecha = fechaLocalDeNegocio(opp.facturafecha);
      if (isNaN(fecha.getTime())) return;
      const mesIndice = fecha.getMonth();
      const facturaYear = fecha.getFullYear();
      if (facturaYear !== ano) return;

      const montoLocal = getConvertedImporte(
        { ...opp, importe: getImporteFacturable(opp) },
        getRateForOpp(opp, getDolarValue, dolarFallbackPais)
      );

      registros.push(
        crearRegistroFinancieroER({
          fecha: String(opp.facturafecha).slice(0, 10),
          ano,
          mesIndice,
          montoLocal,
          bloque: "A",
          concepto: "ingreso_ventas",
          pais,
          meta: {
            id: opp.id,
            origen: "sales",
            facturanumero: opp.facturanumero,
            cliente: opp.cliente,
            importe: montoLocal,
            importeOriginalMoneda: getImporteFacturable(opp),
            moneda_dolar: !!opp.moneda_dolar,
          },
        })
      );
    });
  return registros;
};

/**
 * Registros de ingreso de CONTRATOS (`sales_contratos`) de un (pais, ano) — réplica de
 * `useContratosCalcs.js:53-75` (`calcIngresosContratos`). Un registro por cada línea de
 * `meses[]` con `facturafecha` real en ese año (ER solo cuenta lo realmente facturado).
 *
 * @param {object} p
 * @param {Array} p.contratos - Filas de `sales_contratos` del país (cualquier año).
 * @param {"Chile"|"Perú"} p.pais
 * @param {number} p.ano
 * @param {Function} p.getDolarValue - `(fechaStr) => number`, YA bound al país.
 * @returns {Array} RegistroFinancieroER[]
 */
export const registrosContratos = ({ contratos, pais, ano, getDolarValue }) => {
  const registros = [];
  (contratos || [])
    .filter((c) => !c.eliminada && c.estatus === "100%")
    .forEach((c) => {
      (c.meses || []).forEach((mesObj) => {
        if (!mesObj.facturafecha) return; // ER: solo facturado real, nunca proyectado.
        const fecha = fechaLocalDeNegocio(mesObj.facturafecha);
        if (isNaN(fecha.getTime())) return;
        const mesIndice = fecha.getMonth();
        const year = fecha.getFullYear();
        if (year !== ano) return;

        const importe = getImporteFacturable({
          importe: mesObj.importe,
          descuento_acuerdo_pct: c.descuento_acuerdo_pct,
        });
        const montoLocal = c.moneda_dolar ? importe * getDolarValue(mesObj.facturafecha) : importe;

        registros.push(
          crearRegistroFinancieroER({
            fecha: String(mesObj.facturafecha).slice(0, 10),
            ano,
            mesIndice,
            montoLocal,
            bloque: "A",
            concepto: "ingreso_contratos",
            pais,
            meta: {
              id: c.id,
              mes: mesObj.mes,
              origen: "sales_contratos",
              facturanumero: mesObj.facturanumero,
              cliente: c.cliente,
              importe: montoLocal,
              importeOriginalMoneda: c.moneda_dolar ? importe : montoLocal,
              moneda_dolar: !!c.moneda_dolar,
            },
          })
        );
      });
    });
  return registros;
};

/**
 * Azúcar: ventas + contratos de un (pais, ano) — el ingreso completo del Bloque A
 * ("FACTURACIÓN A", `usePublicidadCalcs`+`useContratosCalcs` combinados, sección 2.1 del diseño).
 */
export const registrosIngresoA = (params) => [...registrosVentas(params), ...registrosContratos(params)];

/**
 * PASO 3.3: BTL (`sales_btl`), Hardware y Gestión (`negocios_carteleria`) — Bloques D/B/C,
 * SOLO ingreso.
 *
 * P2.117-1 (PASO 7, 2026-10-07, Chile solamente — Perú 10.265/10.267 sigue con
 * `useBTLCalcs.js` tal cual, congelado): `row.total_investment` ya no se toma CRUDO — si la
 * fila es USD, convierte con la MISMA regla que Publicidad (`pesificarSiAplicaBTL` en
 * `salesBTLVinculadasSII`: vinculada → `monto_aplicado`; sin vincular → TC de `facturafecha`,
 * acá mismo). 0 filas BTL reales con `moneda_dolar=true` hoy (Chile 2026) — fix de bomba
 * latente, cubierto por un caso sintético (`ingresosER.btl.test.js`).
 */

/**
 * Registros de ingreso de BTL (`sales_btl`) de un (pais, ano) — réplica de
 * `useBTLCalcs.js:26-36` (`calcIngresosBTL`), con el fix de P2.117-1 (ver comentario de arriba).
 */
export const registrosBTL = ({ salesBTL, pais, ano, getDolarValue, dolarFallbackPais }) => {
  const registros = [];
  (salesBTL || []).forEach((row) => {
    if (!row.facturafecha) return;
    const fecha = fechaLocalDeNegocio(row.facturafecha);
    if (isNaN(fecha.getTime()) || fecha.getFullYear() !== ano) return;
    const mesIndice = fecha.getMonth();
    const montoUSD = Number(row.total_investment) || 0;
    // Ya pesificado por `salesBTLVinculadasSII` (vinculada) → `moneda_dolar:false`, sin TC. Sin
    // vincular pero USD → TC de la fecha de la venta (misma cascada que `oppEnLocal`).
    const montoLocal = row.moneda_dolar ? montoUSD * getRateForOpp(row, getDolarValue, dolarFallbackPais) : montoUSD;
    registros.push(
      crearRegistroFinancieroER({
        fecha: String(row.facturafecha).slice(0, 10),
        ano,
        mesIndice,
        montoLocal,
        bloque: "D",
        concepto: "ingreso_btl",
        pais,
        meta: { id: row.id, origen: "sales_btl" },
      })
    );
  });
  return registros;
};
/**
 * Registros de ingreso de HARDWARE (`negocios_carteleria`, Bloque B) de un (pais, ano) — un registro por cada
 * tramo/cuota que suma al mes. F3d (rediseño de Cartelería, "lo histórico no se toca"): los registros de meses
 * ANTERIORES a 2026-10 salen de la lógica histórica congelada
 * (`utils/carteleria/historico/ingresosERCarteleriaHistorico.js`, copia literal del código anterior: réplica de
 * `useHardwareCalcs.js:54-215`, P2.117-2 incluido); los de 2026-10 en adelante salen del motor `calendarioNegocio`
 * (`utils/carteleria/ingresosERCarteleria.js`). Solo alimenta Perú, el modo combinado y Chile <= 2025 (Chile 2026+
 * factura desde SII vinculada, `registrosIngresoSII`).
 *
 * @param {object} p
 * @param {Array} p.negocios - Filas de `negocios_carteleria` del país.
 * @param {"Chile"|"Perú"} p.pais
 * @param {number} p.ano
 * @param {Function} p.getUFValue - `(fechaStr) => number` (solo aplica si pais==="Chile").
 * @param {Function} p.getDolarValue - `(fechaStr) => number`, YA bound al país.
 * @param {Array|null} [p.costosGestion] - `costos_gestion_contenidos` (fecha real de factura OPEX-por-cuota).
 * @param {Date} [p.hoy] - Solo tests: "hoy" del motor (por defecto `new Date()`).
 * @returns {Array} RegistroFinancieroER[]
 */
export const registrosHardware = (p) => {
  const hayHistorico = esMesHistorico(p.ano, 0);
  const hayMotor = !esMesHistorico(p.ano, 11);
  const historico = hayHistorico ? registrosHardwareHistorico(p) : [];
  if (!hayMotor) return historico;
  const motor = registrosHardwareMotor({ ...p, hoy: p.hoy || new Date() }).filter(
    (r) => !esMesHistorico(r.ano, r.mesIndice)
  );
  return [...historico.filter((r) => esMesHistorico(r.ano, r.mesIndice)), ...motor];
};

/**
 * Registros de ingreso de GESTIÓN & CONTENIDOS (`negocios_carteleria`, Bloque C) de un (pais, ano). F3d: los
 * registros de meses < 2026-10 salen de la lógica histórica congelada (variantes PAÍS-ESPECÍFICAS de
 * `useGestionCalcs.js:485-627`; Perú usa `toPEN` con TC MENSUAL, P2.117-3); los de 2026-10 en adelante, del motor
 * (fin de contrato, prórroga del activo vencido, ventana de contenidos). Un negocio con precio TOTAL del contrato
 * (`total_contrato`) no tiene histórico —el código anterior no lo representaba— y sale completo del motor.
 *
 * @param {object} p
 * @param {Array} p.negocios - Filas de `negocios_carteleria` del país.
 * @param {"Chile"|"Perú"} p.pais
 * @param {number} p.ano
 * @param {Function} [p.convertirAMonedaLocal] - Factory bound al país (solo si pais==="Chile").
 * @param {Array} [p.dolarMensualPeru] - Array(12) (solo si pais==="Perú").
 * @param {object} [p.dolarFallback] - `{ Chile, Perú }` (solo si pais==="Perú").
 * @param {Date} [p.hoy] - Solo tests: "hoy" del motor (por defecto `new Date()`).
 * @returns {Array} RegistroFinancieroER[]
 */
export const registrosGestion = (p) => {
  const hoy = p.hoy || new Date();
  const hayHistorico = esMesHistorico(p.ano, 0);
  const hayMotor = !esMesHistorico(p.ano, 11);
  const negocios = p.negocios || [];
  const soloMotor = negocios.filter((n) => esNegocioSoloMotor(n, hoy));
  const resto = soloMotor.length > 0 ? negocios.filter((n) => !soloMotor.includes(n)) : negocios;
  const historico = hayHistorico
    ? registrosGestionHistorico({ ...p, negocios: resto }).filter((r) => esMesHistorico(r.ano, r.mesIndice))
    : [];
  const motor = hayMotor
    ? registrosGestionMotor({ ...p, negocios: resto, hoy }).filter((r) => !esMesHistorico(r.ano, r.mesIndice))
    : [];
  const motorCompleto = soloMotor.length > 0 ? registrosGestionMotor({ ...p, negocios: soloMotor, hoy }) : [];
  return [...historico, ...motor, ...motorCompleto];
};

/**
 * PASO 3.4: las 6 comisiones sobre los registros de ingreso (agencia, edificios, broker, take,
 * APX, comercial) — INVOCANDO `calculateComission`/`comisionEdificiosUtil`/`apx.js`/
 * `costosIntermediacion.js` SIN CAMBIOS, réplica deliberada de los memos standalone de
 * `usePublicidadCalcs.js`/`useContratosCalcs.js` (NO de lo que corre internamente dentro de
 * `calculateSalesCommission` para la comisión comercial — ese es un camino de código aparte que
 * YA existe hoy, duplicado a propósito, y este paso no lo toca ni lo unifica).
 *
 * Signo: estos registros guardan la MAGNITUD POSITIVA de cada comisión, igual que
 * `pubAgencia`/`pubEdificios`/etc. hoy (`valores[mes] += monto`, nunca negativo) y que
 * `costosER.js` (costos en positivo, la resta ocurre en la fórmula del EBITDA, no en el signo
 * del registro) — la reconciliación con la convención "−costo" del tipo `RegistroFinancieroER`
 * del diseño (§1.1) se resuelve en `estadoResultadosER` (paso 3.6), no acá: cambiar el signo acá
 * sin cambiar el consumidor rompería la paridad que este paso tiene que probar.
 *
 * Cada función tiene su PROPIO filtro (no reusa el de `registrosVentas`/`registrosContratos`):
 * agencia/broker/take/apx filtran `!opp.eliminada && opp.facturafecha && opp.client_id`;
 * edificios/comercial filtran `opp.estatus === "100%" && !opp.eliminada && opp.facturafecha`
 * (+`!opp.isBTL` en edificios) — asimetría real con el filtro de ingreso (sin `estatus` ni
 * `client_id`), preservada tal cual.
 */

// ─── VENTAS (`sales`) ───────────────────────────────────────────────────────────────────────

// PASO 7 (fix, 2026-10-07) — `getTakeLocal` exige la venta con su `moneda_dolar` REAL (su contrato
// documentado: "se le pasa la venta ORIGINAL... getRateForOpp devuelve 1 si la venta no es en
// USD"), porque el take (`costos_intermediacion`, siempre en USD) es un hecho INDEPENDIENTE del
// importe de la venta — PASO 6c pesifica `opp.moneda_dolar` a `false` para las ventas vinculadas
// (correcto para agencia/edificios/comercial, que SÍ deben dejar de reconvertir), pero eso rompía
// el take: con `moneda_dolar:false`, `getRateForOpp` devolvía 1 en vez del TC real, dejando el
// take en USD crudo tratado como pesos (bug real, encontrado en `fase38ComisionLocalSinCambio.
// test.js` — 17 celdas con diff de cientos de miles de pesos, Costo intermediación/Comisión
// edificios/Comisión comercial, meses 3-7 y 9). `pesificarSiAplica` preserva el original en
// `monedaDolarOriginal`; este helper lo restaura SOLO para el cálculo del take.
const oppParaTake = (opp) => (opp.monedaDolarOriginal ? { ...opp, moneda_dolar: true } : opp);

// Convierte una opp con `moneda_dolar` a moneda local — réplica de `oppEnLocal` (usePublicidadCalcs.js:234-238).
const oppEnLocal = (opp, getDolarValue, dolarFallbackPais) => {
  if (!opp.moneda_dolar) return opp;
  const rate = getRateForOpp(opp, getDolarValue, dolarFallbackPais);
  return { ...opp, importe: (parseFloat(opp.importe) || 0) * rate, moneda_dolar: false };
};

const fechaDeVentaValida = (opp, ano) => {
  if (!opp.facturafecha) return null;
  const fecha = fechaLocalDeNegocio(opp.facturafecha);
  if (isNaN(fecha.getTime()) || fecha.getFullYear() !== ano) return null;
  return fecha;
};

const pushComision = (registros, { fecha, ano, mesIndice, montoLocal, bloque, concepto, pais, meta }) => {
  if (!montoLocal) return;
  registros.push(crearRegistroFinancieroER({ fecha, ano, mesIndice, montoLocal, bloque, concepto, pais, meta }));
};

/** Réplica de `valoresMensualesComisionAgencia` (`usePublicidadCalcs.js:240-261`). */
export const registrosComisionAgenciaVentas = ({
  oportunidades,
  pais,
  ano,
  getDolarValue,
  dolarFallbackPais,
  clientes,
  intermediarios,
  pctMap = null,
}) => {
  const registros = [];
  (oportunidades || [])
    .filter((opp) => !opp.eliminada && opp.facturafecha && opp.client_id)
    .forEach((opp) => {
      const fecha = fechaDeVentaValida(opp, ano);
      if (!fecha) return;
      const local = oppEnLocal(opp, getDolarValue, dolarFallbackPais);
      const neto = calcularImporteNetoIntermediario(
        local,
        Object.values(clientes),
        Object.values(intermediarios),
        pais,
        pctMap
      );
      const importeLocal = getImporteFacturable(local);
      const montoLocal = importeLocal - neto;
      pushComision(registros, {
        fecha: String(opp.facturafecha).slice(0, 10),
        ano,
        mesIndice: fecha.getMonth(),
        montoLocal,
        bloque: "A",
        concepto: "comision_agencia",
        pais,
        meta: {
          id: opp.id,
          origen: "sales",
          facturanumero: opp.facturanumero,
          cliente: opp.cliente,
          intermediario: obtenerNombreIntermediario(opp, Object.values(clientes), Object.values(intermediarios), pais),
          porcentaje: calcularPorcentajeComisionIntermediario(
            opp,
            Object.values(clientes),
            Object.values(intermediarios),
            pais,
            pctMap
          ),
          importe: importeLocal,
          importeOriginalMoneda: getImporteFacturable(opp),
          moneda_dolar: !!opp.moneda_dolar,
        },
      });
    });
  return registros;
};

/** Réplica de `valoresMensualesComisionBroker` (`usePublicidadCalcs.js:362-388`). */
export const registrosComisionBrokerVentas = ({
  oportunidades,
  pais,
  ano,
  getDolarValue,
  dolarFallbackPais,
  clientes,
  intermediarios,
  pctMap = null,
}) => {
  const registros = [];
  const clientesArr = Object.values(clientes);
  const intermArr = Object.values(intermediarios);
  (oportunidades || [])
    .filter((opp) => !opp.eliminada && opp.facturafecha && opp.client_id)
    .forEach((opp) => {
      const fecha = fechaDeVentaValida(opp, ano);
      if (!fecha) return;
      const local = oppEnLocal(opp, getDolarValue, dolarFallbackPais);
      const importeLocal = parseFloat(local.importe) || 0;
      const pct = resolverPorcentajeBroker(opp, clientesArr, intermArr, fecha, pctMap);
      const montoLocal = (importeLocal * pct) / 100;
      const clienteObj = clientesArr.find((c) => c.id === opp.client_id && c.pais === pais);
      const broker = intermArr.find((i) => i.id === parseInt(clienteObj?.broker_id, 10) && i.pais === pais);
      pushComision(registros, {
        fecha: String(opp.facturafecha).slice(0, 10),
        ano,
        mesIndice: fecha.getMonth(),
        montoLocal,
        bloque: "A",
        concepto: "comision_broker",
        pais,
        meta: {
          id: opp.id,
          origen: "sales",
          facturanumero: opp.facturanumero,
          cliente: opp.cliente,
          brokerNombre: broker?.nombre,
          porcentajeBroker: pct,
          // `importeLocal` acá es `local.importe` crudo (NO `getImporteFacturable`) — réplica
          // deliberada de esta función tal como estaba (10.243): no se "corrige" la base de
          // display para no mover `montoLocal`, que ya usaba este mismo crudo.
          importe: importeLocal,
          importeOriginalMoneda: getImporteFacturable(opp),
          moneda_dolar: !!opp.moneda_dolar,
        },
      });
    });
  return registros;
};

/** Réplica de `valoresMensualesTake` (`usePublicidadCalcs.js:621-634`). SIN equivalente en contratos. */
export const registrosComisionTakeVentas = ({
  oportunidades,
  pais,
  ano,
  getDolarValue,
  dolarFallbackPais,
  costosMap,
}) => {
  const registros = [];
  if (!costosMap) return registros;
  (oportunidades || [])
    .filter((opp) => !opp.eliminada && opp.facturafecha && opp.client_id)
    .forEach((opp) => {
      const fecha = fechaDeVentaValida(opp, ano);
      if (!fecha) return;
      const montoLocal = getTakeLocal(oppParaTake(opp), "sales", opp.id, costosMap, getDolarValue, dolarFallbackPais);
      const local = oppEnLocal(opp, getDolarValue, dolarFallbackPais);
      pushComision(registros, {
        fecha: String(opp.facturafecha).slice(0, 10),
        ano,
        mesIndice: fecha.getMonth(),
        montoLocal,
        bloque: "A",
        concepto: "take",
        pais,
        meta: {
          id: opp.id,
          origen: "sales",
          facturanumero: opp.facturanumero,
          cliente: opp.cliente || "Sin cliente",
          importe: getImporteFacturable(local),
          importeOriginalMoneda: getImporteFacturable(opp),
          moneda_dolar: !!opp.moneda_dolar,
        },
      });
    });
  return registros;
};

/** Réplica de `valoresMensualesApx` (`usePublicidadCalcs.js:736-752`, mismo patrón de `apxPorMes`). */
export const registrosComisionApxVentas = ({
  oportunidades,
  pais,
  ano,
  getDolarValue,
  dolarFallbackPais,
  intermediarios,
  pctMap = null,
}) => {
  const registros = [];
  (oportunidades || [])
    .filter((opp) => !opp.eliminada && opp.facturafecha && opp.client_id)
    .forEach((opp) => {
      const fecha = fechaDeVentaValida(opp, ano);
      if (!fecha) return;
      const local = oppEnLocal(opp, getDolarValue, dolarFallbackPais);
      const montoLocal = getApxMonto(local, intermediarios, pctMap);
      const apxId = parseInt(opp.apx_id, 10);
      const inter = Object.values(intermediarios).find((i) => i.id === apxId);
      pushComision(registros, {
        fecha: String(opp.facturafecha).slice(0, 10),
        ano,
        mesIndice: fecha.getMonth(),
        montoLocal,
        bloque: "A",
        concepto: "apx",
        pais,
        meta: {
          id: opp.id,
          origen: "sales",
          facturanumero: opp.facturanumero,
          cliente: opp.cliente || "Sin cliente",
          porcentaje: inter?.comision || 0,
          importe: getImporteFacturable(local),
          importeOriginalMoneda: getImporteFacturable(opp),
          moneda_dolar: !!opp.moneda_dolar,
        },
      });
    });
  return registros;
};

/** Réplica de `valoresMensualesComisiones` (edificios, `usePublicidadCalcs.js:803-832`). */
export const registrosComisionEdificiosVentas = ({
  oportunidades,
  pais,
  ano,
  getDolarValue,
  dolarFallbackPais,
  clientes,
  intermediarios,
  edificios,
  snapMap = null,
  costosMap,
  pctMap = null,
}) => {
  const registros = [];
  (oportunidades || [])
    .filter((opp) => opp.estatus === "100%" && !opp.eliminada && opp.facturafecha && !opp.isBTL)
    .forEach((opp) => {
      const fecha = fechaDeVentaValida(opp, ano);
      if (!fecha) return;
      const local = oppEnLocal(opp, getDolarValue, dolarFallbackPais);
      const netoFactura = calcularImporteNetoIntermediario(
        local,
        Object.values(clientes),
        Object.values(intermediarios),
        pais,
        pctMap
      );
      const takeLocal = getTakeLocal(oppParaTake(opp), "sales", opp.id, costosMap, getDolarValue, dolarFallbackPais);
      const apxLocal = getApxMonto(local, intermediarios, pctMap);
      const montoLocal = calcularComisionEdificiosTotal(local, edificios, netoFactura, snapMap, takeLocal, apxLocal);
      const edIds = opp.edificiosparticipantes ? [...new Set(opp.edificiosparticipantes.split(",").map(Number))] : [];
      const fechaFactura = resolverFechaFactura(opp);
      const soloFijos = edIds.length > 0 && edIds.every((id) => esEdificioComisionFija(edificios[id], fechaFactura));
      pushComision(registros, {
        fecha: String(opp.facturafecha).slice(0, 10),
        ano,
        mesIndice: fecha.getMonth(),
        montoLocal,
        bloque: "A",
        concepto: "comision_edificios",
        pais,
        meta: {
          id: opp.id,
          origen: "sales",
          facturanumero: opp.facturanumero,
          cliente: opp.cliente || "Sin cliente",
          totalComision: montoLocal,
          soloFijos,
          sinEdificios: edIds.length === 0,
        },
      });
    });
  return registros;
};

/** Réplica de `valoresMensualesComisionComercial.valores` (`usePublicidadCalcs.js:1059-1158`). */
/**
 * PASO 6 (2026-10-07) — `meta` enriquecida para que el modal de detalle ("Resumen por
 * Ejecutivo" + tabla por oportunidad) lea DIRECTO de estos registros, nunca de un cálculo
 * paralelo. `desglose` sale de `getSalesCommissionBreakdown` CON `pctMap` — mismo argumento que
 * `calculateSalesCommission` (que arma `montoLocal`), fuente única (10.1): antes de este paso,
 * `usePublicidadCalcs.js` llamaba al breakdown SIN `pctMap`, dando un desglose que no sumaba lo
 * mismo que el total en ventas con `comision_snapshot` sellado (ver doc de cierre, ejemplo real).
 */
export const registrosComisionComercialVentas = ({
  oportunidades,
  pais,
  ano,
  getDolarValue,
  dolarFallbackPais,
  clientes,
  intermediarios,
  edificios,
  snapMap = null,
  costosMap,
  pctMap = null,
}) => {
  const registros = [];
  (oportunidades || [])
    .filter((opp) => opp.estatus === "100%" && !opp.eliminada && opp.facturafecha)
    .forEach((opp) => {
      const fecha = fechaDeVentaValida(opp, ano);
      if (!fecha) return;
      const local = oppEnLocal(opp, getDolarValue, dolarFallbackPais);
      const takeLocal = getTakeLocal(oppParaTake(opp), "sales", opp.id, costosMap, getDolarValue, dolarFallbackPais);
      const apxLocal = getApxMonto(local, intermediarios, pctMap);
      const montoLocal = calculateComission.calculateSalesCommission(
        local,
        clientes,
        intermediarios,
        edificios,
        undefined,
        snapMap,
        takeLocal,
        pctMap
      );
      const comisionAgencia = calculateComission.calculateAgencyCommission(
        local,
        clientes,
        intermediarios,
        undefined,
        pctMap
      );
      const netoAgencia = calcularImporteNetoIntermediario(
        local,
        Object.values(clientes || {}),
        Object.values(intermediarios || {}),
        pais,
        pctMap
      );
      const comisionEdificios = calcularComisionEdificiosTotal(
        local,
        edificios,
        netoAgencia,
        snapMap,
        takeLocal,
        apxLocal
      );
      const importeLocal = getImporteFacturable(local);
      pushComision(registros, {
        fecha: String(opp.facturafecha).slice(0, 10),
        ano,
        mesIndice: fecha.getMonth(),
        montoLocal,
        bloque: "A",
        concepto: "comision_comercial",
        pais,
        meta: {
          id: opp.id,
          origen: "sales",
          facturanumero: opp.facturanumero,
          cliente: opp.cliente,
          ejecutivo: opp.ejecutivo,
          importe: importeLocal,
          importeOriginalMoneda: getImporteFacturable(opp),
          moneda_dolar: !!opp.moneda_dolar,
          comisionAgencia,
          comisionEdificios,
          neto: importeLocal - comisionAgencia - comisionEdificios - takeLocal - apxLocal,
          desglose: calculateComission.getSalesCommissionBreakdown(
            local,
            clientes,
            intermediarios,
            edificios,
            undefined,
            snapMap,
            takeLocal,
            pctMap
          ),
        },
      });
    });
  return registros;
};

// ─── CONTRATOS (`sales_contratos`) ─────────────────────────────────────────────────────────

const mesContratoValido = (c, mesObj, ano) => {
  if (!mesObj.facturafecha) return null;
  const fecha = fechaLocalDeNegocio(mesObj.facturafecha);
  if (isNaN(fecha.getTime()) || fecha.getFullYear() !== ano) return null;
  return fecha;
};

const importeLocalContrato = (c, mesObj, getDolarValue) => {
  // PASO 6c ("pesificar en la factura"): un mes USD vinculado a su factura SII real ya trae
  // `montoAplicadoCLP` (resuelto por `contratosVinculadosSII`, el único punto que arma el CLP de
  // un mes de contrato para el motor) — usarlo directo, sin volver a convertir con el TC del día.
  if (c.moneda_dolar && mesObj.montoAplicadoCLP != null) return mesObj.montoAplicadoCLP;
  const importe = Number(mesObj.importe) || 0;
  return c.moneda_dolar ? importe * getDolarValue(mesObj.facturafecha) : importe;
};

/** Réplica de `calcComisionAgenciaContratos` (`useContratosCalcs.js:160-191`). */
export const registrosComisionAgenciaContratos = ({
  contratos,
  pais,
  ano,
  getDolarValue,
  clientes,
  intermediarios,
  pctMap = null,
}) => {
  const registros = [];
  const clientesArr = Object.values(clientes);
  const intermArr = Object.values(intermediarios);
  (contratos || [])
    .filter((c) => !c.eliminada && c.estatus === "100%")
    .forEach((c) => {
      (c.meses || []).forEach((mesObj) => {
        const fecha = mesContratoValido(c, mesObj, ano);
        if (!fecha) return;
        const importeLocal = importeLocalContrato(c, mesObj, getDolarValue);
        const oppObj = {
          id: `contrato-${c.id}-${mesObj.mes}`,
          importe: importeLocal,
          client_id: c.client_id,
          descuento_acuerdo_pct: c.descuento_acuerdo_pct,
          facturafecha: mesObj.facturafecha,
          isBTL: false,
        };
        const neto = calcularImporteNetoIntermediario(oppObj, clientesArr, intermArr, pais, pctMap);
        pushComision(registros, {
          fecha: String(mesObj.facturafecha).slice(0, 10),
          ano,
          mesIndice: fecha.getMonth(),
          montoLocal: importeLocal - neto,
          bloque: "A",
          concepto: "comision_agencia",
          pais,
          meta: {
            id: c.id,
            mes: mesObj.mes,
            origen: "sales_contratos",
            facturanumero: mesObj.facturanumero,
            cliente: c.cliente,
            intermediario: obtenerNombreIntermediario(oppObj, clientesArr, intermArr, pais),
            porcentaje: calcularPorcentajeComisionIntermediario(oppObj, clientesArr, intermArr, pais, pctMap),
            importe: importeLocal,
            importeOriginalMoneda: importeLocal,
            moneda_dolar: false,
          },
        });
      });
    });
  return registros;
};

/** Réplica de `calcComisionBrokerContratos` (`useContratosCalcs.js:196-227`). */
export const registrosComisionBrokerContratos = ({
  contratos,
  pais,
  ano,
  getDolarValue,
  clientes,
  intermediarios,
  pctMap = null,
}) => {
  const registros = [];
  const clientesArr = Object.values(clientes);
  const intermArr = Object.values(intermediarios);
  (contratos || [])
    .filter((c) => !c.eliminada && c.estatus === "100%")
    .forEach((c) => {
      (c.meses || []).forEach((mesObj) => {
        const fecha = mesContratoValido(c, mesObj, ano);
        if (!fecha) return;
        const pct = resolverPorcentajeBroker(
          { id: `contrato-${c.id}-${mesObj.mes}`, client_id: c.client_id, pais },
          clientesArr,
          intermArr,
          fecha,
          pctMap
        );
        const importeLocal = importeLocalContrato(c, mesObj, getDolarValue);
        const clienteObj = clientesArr.find((cl) => cl.id === c.client_id && cl.pais === pais);
        const broker = intermArr.find((i) => i.id === parseInt(clienteObj?.broker_id, 10) && i.pais === pais);
        pushComision(registros, {
          fecha: String(mesObj.facturafecha).slice(0, 10),
          ano,
          mesIndice: fecha.getMonth(),
          montoLocal: (importeLocal * pct) / 100,
          bloque: "A",
          concepto: "comision_broker",
          pais,
          meta: {
            id: c.id,
            mes: mesObj.mes,
            origen: "sales_contratos",
            facturanumero: mesObj.facturanumero,
            cliente: c.cliente,
            brokerNombre: broker?.nombre,
            porcentajeBroker: pct,
            importe: importeLocal,
            importeOriginalMoneda: importeLocal,
            moneda_dolar: false,
          },
        });
      });
    });
  return registros;
};

/** Réplica de `calcComisionApxContratos` (`useContratosCalcs.js:357-379`). */
export const registrosComisionApxContratos = ({
  contratos,
  pais,
  ano,
  getDolarValue,
  intermediarios,
  pctMap = null,
}) => {
  const registros = [];
  const intermArr = Object.values(intermediarios);
  (contratos || [])
    .filter((c) => !c.eliminada && c.estatus === "100%" && c.apx_id != null)
    .forEach((c) => {
      (c.meses || []).forEach((mesObj) => {
        const fecha = mesContratoValido(c, mesObj, ano);
        if (!fecha) return;
        const importeLocal = importeLocalContrato(c, mesObj, getDolarValue);
        const montoLocal = getApxMonto(
          { id: `contrato-${c.id}-${mesObj.mes}`, apx_id: c.apx_id, importe: importeLocal },
          intermArr,
          pctMap
        );
        const apxId = parseInt(c.apx_id, 10);
        const inter = intermArr.find((i) => i.id === apxId);
        pushComision(registros, {
          fecha: String(mesObj.facturafecha).slice(0, 10),
          ano,
          mesIndice: fecha.getMonth(),
          montoLocal,
          bloque: "A",
          concepto: "apx",
          pais,
          meta: {
            id: c.id,
            mes: mesObj.mes,
            origen: "sales_contratos",
            facturanumero: mesObj.facturanumero,
            cliente: c.cliente,
            porcentaje: inter?.comision || 0,
            importe: importeLocal,
            importeOriginalMoneda: importeLocal,
            moneda_dolar: false,
          },
        });
      });
    });
  return registros;
};

/** Réplica de `calcComisionEdificiosContratos` (`useContratosCalcs.js:109-157`). take=0 (pendiente FASE 6). */
export const registrosComisionEdificiosContratos = ({
  contratos,
  pais,
  ano,
  getDolarValue,
  edificios,
  clientes,
  intermediarios,
  snapMap = null,
  pctMap = null,
}) => {
  const registros = [];
  if (!edificios || Object.keys(edificios).length === 0) return registros;
  const clientesArr = Object.values(clientes);
  const intermArr = Object.values(intermediarios);
  (contratos || [])
    .filter((c) => !c.eliminada && c.estatus === "100%")
    .forEach((c) => {
      (c.meses || []).forEach((mesObj) => {
        if (!mesObj.edificios_ids) return;
        const fecha = mesContratoValido(c, mesObj, ano);
        if (!fecha) return;
        const importeLocal = importeLocalContrato(c, mesObj, getDolarValue);
        const oppObj = {
          id: `contrato-${c.id}-${mesObj.mes}`,
          edificiosparticipantes: mesObj.edificios_ids,
          pantallastotales: mesObj.pantallas_totales || 0,
          pantallas_ascensores: mesObj.pantallas_ascensores || "",
          pantallas_espera: mesObj.pantallas_espera || "",
          pantallas_circulacion: mesObj.pantallas_circulacion || "",
          pantallas_cowork: mesObj.pantallas_cowork || "",
          facturafecha: mesObj.facturafecha,
          importe: importeLocal,
          client_id: c.client_id,
          descuento_acuerdo_pct: c.descuento_acuerdo_pct,
          apx_id: c.apx_id,
          isBTL: false,
        };
        const neto = calcularImporteNetoIntermediario(oppObj, clientesArr, intermArr, pais, pctMap);
        const apxLocal = getApxMonto(oppObj, intermArr, pctMap);
        const montoLocal = calcularComisionEdificiosTotal(oppObj, edificios, neto, snapMap, 0, apxLocal);
        const edIds = mesObj.edificios_ids ? [...new Set(mesObj.edificios_ids.split(",").map(Number))] : [];
        pushComision(registros, {
          fecha: String(mesObj.facturafecha).slice(0, 10),
          ano,
          mesIndice: fecha.getMonth(),
          montoLocal,
          bloque: "A",
          concepto: "comision_edificios",
          pais,
          meta: {
            id: c.id,
            mes: mesObj.mes,
            origen: "sales_contratos",
            facturanumero: mesObj.facturanumero || "—",
            cliente: c.cliente || "Sin cliente",
            totalComision: montoLocal,
            soloFijos: false,
            sinEdificios: edIds.length === 0,
          },
        });
      });
    });
  return registros;
};

/** Réplica de `calcComisionComercialContratos` (`useContratosCalcs.js:425-479`). take=0 (pendiente FASE 6). */
export const registrosComisionComercialContratos = ({
  contratos,
  pais,
  ano,
  getDolarValue,
  edificios,
  clientes,
  intermediarios,
  snapMap = null,
  pctMap = null,
}) => {
  const registros = [];
  (contratos || [])
    .filter((c) => !c.eliminada && c.estatus === "100%")
    .forEach((c) => {
      (c.meses || []).forEach((mesObj) => {
        const fecha = mesContratoValido(c, mesObj, ano);
        if (!fecha) return;
        const importeLocal = importeLocalContrato(c, mesObj, getDolarValue);
        const oppObj = {
          id: `contrato-${c.id}-${mesObj.mes}`,
          importe: importeLocal,
          client_id: c.client_id,
          descuento_acuerdo_pct: c.descuento_acuerdo_pct,
          apx_id: c.apx_id,
          pais,
          facturafecha: mesObj.facturafecha,
          ejecutivo: c.ejecutivo,
          mes_cierre: c.mes_cierre,
          moneda_dolar: false,
          isBTL: false,
          edificiosparticipantes: mesObj.edificios_ids || "",
          pantallastotales: mesObj.pantallas_totales || 0,
          pantallas_ascensores: mesObj.pantallas_ascensores || "",
          pantallas_espera: mesObj.pantallas_espera || "",
          pantallas_circulacion: mesObj.pantallas_circulacion || "",
          pantallas_cowork: mesObj.pantallas_cowork || "",
        };
        const montoLocal = calculateComission.calculateSalesCommission(
          oppObj,
          clientes,
          intermediarios,
          edificios,
          undefined,
          snapMap,
          0,
          pctMap
        );
        const comisionAgencia = calculateComission.calculateAgencyCommission(
          oppObj,
          clientes,
          intermediarios,
          undefined,
          pctMap
        );
        const netoAgencia = calcularImporteNetoIntermediario(
          oppObj,
          Object.values(clientes || {}),
          Object.values(intermediarios || {}),
          pais,
          pctMap
        );
        const apxLocal = getApxMonto(oppObj, intermediarios, pctMap);
        const comisionEdificios = calcularComisionEdificiosTotal(oppObj, edificios, netoAgencia, snapMap, 0, apxLocal);
        pushComision(registros, {
          fecha: String(mesObj.facturafecha).slice(0, 10),
          ano,
          mesIndice: fecha.getMonth(),
          montoLocal,
          bloque: "A",
          concepto: "comision_comercial",
          pais,
          meta: {
            id: c.id,
            mes: mesObj.mes,
            origen: "sales_contratos",
            facturanumero: mesObj.facturanumero,
            cliente: c.cliente,
            ejecutivo: c.ejecutivo,
            importe: importeLocal,
            importeOriginalMoneda: importeLocal,
            moneda_dolar: false,
            comisionAgencia,
            comisionEdificios,
            neto: importeLocal - comisionAgencia - comisionEdificios - apxLocal,
            desglose: calculateComission.getSalesCommissionBreakdown(
              oppObj,
              clientes,
              intermediarios,
              edificios,
              undefined,
              snapMap,
              0,
              pctMap
            ),
          },
        });
      });
    });
  return registros;
};

// ─── PASO 3 (Criterio 1), punto 2 (F1) — los 2 gaps reales de registro de comisión ──────────
// De las 13 series de comisión, 11 YA tenían función de registro exacta (3.1-3.5). Faltaban
// estas 2 — ninguna necesita aproximación, ambas tienen monto por mes/venta ya resuelto hoy.

/**
 * Comisión FIJA de edificios (Bloque A) — réplica de `calcularComisionesFijasMensuales`
 * (`comisionEdificiosUtil.js:319-333`): para cada mes, suma `comision_fija_monto` de los
 * edificios con comisión fija vigente ese mes (convertido de USD a local con el TC del día 1
 * del mes, igual que la función original — ESE monto en pesos NO cambia, se replica tal cual).
 *
 * `fecha: null` a propósito (punto 2 del encargo): la comisión fija no nace de un documento con
 * fecha propia — es una condición de vigencia evaluada mes a mes (como RRHH/Depreciación), así
 * que su US$ usa el TC promedio real del mes (Criterio 1b), no el TC del día 1 que usa el
 * cálculo en pesos (ese TC del día 1 solo convierte USD de catálogo→CLP/PEN, no es una fecha de
 * documento — son dos conversiones distintas, con dos propósitos distintos).
 *
 * Un registro por (edificio, mes) en vez de un total mensual — igual granularidad que el resto
 * de las 11 funciones de comisión (un registro por hecho, nunca un total pre-sumado).
 */
export const registrosComisionEdificiosFijas = ({ edificios, pais, ano, getDolarValue }) => {
  const registros = [];
  const edificiosMap = edificios || {};
  const listaEdificios = Object.entries(edificiosMap);
  for (let mes = 0; mes < 12; mes++) {
    const fechaPrimero = `${ano}-${String(mes + 1).padStart(2, "0")}-01`;
    listaEdificios.forEach(([id, ed]) => {
      if (!esEdificioComisionFija(ed, fechaPrimero)) return;
      const monto = Number(ed.comision_fija_monto) || 0;
      if (monto <= 0) return;
      const moneda = (ed.comision_fija_moneda || "USD").toUpperCase();
      const montoLocal = moneda === "USD" ? monto * getDolarValue(fechaPrimero) : monto;
      pushComision(registros, {
        fecha: null,
        ano,
        mesIndice: mes,
        montoLocal,
        bloque: "A",
        concepto: "comision_edificios",
        pais,
        meta: { id, origen: "edificios_comision_fija", edificio: ed.edificio || `Edificio ${id}`, montoLocal },
      });
    });
  }
  return registros;
};

/**
 * Comisión de edificios de BTL (Bloque D) — réplica de `useBTLCalcs.calcComisionEdificiosBTL`
 * (`useBTLCalcs.js:38-53`): por cada venta BTL con `buildings` (jsonb), suma
 * `importe * (comision/100)` de cada edificio participante.
 *
 * `fecha` SÍ disponible (a diferencia de la fija): es la fecha de la venta BTL
 * (`row.facturafecha`), el MISMO documento/fecha que ya usa `registrosBTL` (el ingreso del
 * mismo bloque D) — Criterio 1a, TC de esa fecha exacta.
 */
// P2.117-1 (PASO 7, Chile solamente): rate efectivo de `building.importe` (USD nativo, cada
// edificio es una porción de `total_investment`) — vinculada: el TC IMPLÍCITO real de la
// factura (`total_investment` pesificado / `montoOrigenUSD` preservado por
// `salesBTLVinculadasSII`), misma proporción para cada edificio; sin vincular: TC de la fecha de
// la venta (igual que el ingreso, `registrosBTL`); venta en pesos: 1 (sin conversión).
const rateComisionEdificiosBTL = (row, getDolarValue, dolarFallbackPais) => {
  if (row.montoOrigenUSD != null && row.montoOrigenUSD > 0) {
    return (Number(row.total_investment) || 0) / row.montoOrigenUSD;
  }
  return row.moneda_dolar ? getRateForOpp(row, getDolarValue, dolarFallbackPais) : 1;
};

export const registrosComisionEdificiosBTL = ({ salesBTL, pais, ano, getDolarValue, dolarFallbackPais }) => {
  const registros = [];
  (salesBTL || []).forEach((row) => {
    if (!row.facturafecha || !row.buildings) return;
    const fecha = fechaLocalDeNegocio(row.facturafecha);
    if (isNaN(fecha.getTime()) || fecha.getFullYear() !== ano) return;
    const buildings = typeof row.buildings === "string" ? JSON.parse(row.buildings) : row.buildings;
    const rate = rateComisionEdificiosBTL(row, getDolarValue, dolarFallbackPais);
    (buildings || []).forEach((building, idx) => {
      const importe = (Number(building.importe) || 0) * rate;
      const porcentaje = Number(building.comision) || 0;
      const montoLocal = importe * (porcentaje / 100);
      pushComision(registros, {
        fecha: String(row.facturafecha).slice(0, 10),
        ano,
        mesIndice: fecha.getMonth(),
        montoLocal,
        bloque: "D",
        concepto: "comision_edificios_btl",
        pais,
        meta: { id: row.id, buildingIndex: idx, origen: "sales_btl" },
      });
    });
  });
  return registros;
};

/**
 * FASE 3 PASO 3 (criterio 1), punto 4 — filas-hoja que SUMAN varias de las 13 series de
 * comisión (3.1-3.5 + punto 2/F1 de este mismo encargo), para que cada fila del Bloque A/D
 * tenga UNA función de registros. Composición pura, mismo objeto de params para todas (cada
 * función interna destructura solo lo que necesita) — ninguna lógica nueva (10.1).
 */
export const registrosCostoIntermediacion = (p) => [
  ...registrosComisionAgenciaVentas(p),
  ...registrosComisionAgenciaContratos(p),
  ...registrosComisionBrokerVentas(p),
  ...registrosComisionBrokerContratos(p),
  ...registrosComisionTakeVentas(p),
  ...registrosComisionApxVentas(p),
  ...registrosComisionApxContratos(p),
];

export const registrosComisionEdificiosA = (p) => [
  ...registrosComisionEdificiosVentas(p),
  ...registrosComisionEdificiosContratos(p),
  ...registrosComisionEdificiosFijas(p),
];

export const registrosComisionComercialA = (p) => [
  ...registrosComisionComercialVentas(p),
  ...registrosComisionComercialContratos(p),
];

// ─── PASO 5 (3.9) — Chile 2026+: facturación desde las facturas SII EMITIDAS vinculadas ───
//
// H8 (decisión de Fase 0) + corrección del 2026-10-05 (decisión 2 de la medición previa queda
// ANULADA): el ingreso 2026+ YA NO sale de `sales`/`sales_contratos`/`negocios_carteleria`/
// `sales_btl` directo — sale de `facturas_sii_vinculaciones.monto_aplicado` (NETO, SIEMPRE en
// moneda LOCAL — verificado con datos reales, PARTE 1a: incluso en facturas de exportación
// tipo_dte=110, `monto_aplicado` coincide con `monto_total`/`monto_exento`, nunca con
// `monto_usd`), fecha = `fecha_emision` de la factura. La vinculación solo asigna BLOQUE (vía
// `venta_tipo`) y permite ubicar la venta/contrato de origen para sus comisiones (más abajo).
// 2025 y antes, y Perú (cualquier año): sin cambios, siguen en `registrosIngresoA/B/C/D` de
// arriba, basados en `sales`/`sales_contratos`/`negocios_carteleria`/`sales_btl`.
//
// Verificado con datos reales (PARTE 1, `docs/decisiones/2026-10-06-reingenieria-ebitda-paso-5-facturacion-sii.md`):
// `venta_tipo` ∈ {sales, sales_contratos} = Bloque A; negocios_carteleria_hw = Bloque B;
// sales_btl = Bloque D (0 casos en 2026, Chile — la única venta BTL de Chile es de 2025). 0
// vinculaciones parciales (Σ monto_aplicado de una factura siempre == su monto_neto); 0 facturas
// multi-bloque; SÍ hay facturas multi-línea (varias filas de `facturas_sii_vinculaciones` por una
// misma factura, a veces incluso a la MISMA venta — folios 570/571 reales) — por eso se arma UN
// REGISTRO POR FILA de vinculación, nunca por factura.
//
// `negocios_carteleria` DELIBERADAMENTE AUSENTE de este mapa (PASO 5c, 2026-10-07, bug de PASO 5
// identificado por Cowork): su `venta_tipo` NO alcanza para asignar bloque — agrupa hardware OPEX
// (Bloque B) y gestión (Bloque C) bajo el MISMO string (folio 571, sep 2026: $294.329 gestión +
// $100.562 OPEX, mismo venta_tipo, misma venta — los dos quedaban mal en C). Su bloque sale de su
// `linea_id` → `lineas_facturacion.tipo`, resuelto por `resolverBloqueVinculacion` (abajo), NUNCA
// de este mapa — un lookup `VENTA_TIPO_A_BLOQUE_ER['negocios_carteleria']` quedaría `undefined`,
// a propósito (si algo lo necesita, es un bug a corregir, no un valor a agregar acá).
export const VENTA_TIPO_A_BLOQUE_ER = {
  sales: "A",
  sales_contratos: "A",
  negocios_carteleria_hw: "B",
  sales_btl: "D",
};

/**
 * Notas de crédito EMITIDAS (tipo_dte=61) — exactamente la regla del doc de corrección
 * (`2026-10-05-reingenieria-ebitda-correccion-nc-emitidas.md`), que ANULA la decisión 2 de la
 * medición previa ("restar toda NC emitida, vinculada o no"). La detección del par
 * `anula_error` sale SOLO de `construirExclusionNcAnulaError` (10.223) — función PURA, agnóstica
 * de recibidas/emitidas (mismas columnas `tipo_dte`/`tipo_relacion_nc`/`documento_referenciado_id`
 * en ambas tablas, verificado por schema) — llamada acá tal cual, SIN reimplementarla ni
 * cambiar una línea suya (aditivo, 10.1): se le pasa el universo de EMITIDAS en vez de
 * RECIBIDAS, nada más.
 *
 * 4 ramas (idénticas a las que ya aplica `costosER.js` para recibidas, réplica deliberada):
 *  1. `anula_error` CON `documento_referenciado_id`: la NC Y la factura anulada quedan FUERA
 *     enteras — ya lo da `construirExclusionNcAnulaError` (`facturasAnuladas`/`notasCredito`).
 *  2. `anula_error` SIN referencia: NO se excluye en costos (resta en su propia `cuenta_er`,
 *     10.225) — pero una NC emitida NO tiene `cuenta_er`/bloque propio (nunca pasa por una
 *     clasificación como las recibidas; columnas `venta_tipo`/`venta_ref_id` siempre null en
 *     las 4 NC reales de hoy, verificado). Sin un bloque al que restarle, y SIN ningún caso real
 *     hoy (0 NC emitidas con este patrón), se trata igual que "sin clasificar" (rama 4) — se
 *     excluye. Documentado como decisión explícita (no silenciosa, 10.177), no como
 *     implementación de 10.225 para emitidas: ver doc de cierre, pendiente de roadmap si
 *     aparece un caso real.
 *  3. `ajuste_comercial` CON referencia: la NC resta en su mes, en el BLOQUE de la venta de la
 *     factura referenciada (se resuelve en `registrosIngresoSII`, vía `facturaIdABloque`) — la
 *     factura referenciada cuenta normal (nunca se excluye, a diferencia de `anula_error`).
 *  4. Sin clasificar (`tipo_relacion_nc` null, cualquier `documento_referenciado_id`): la propia
 *     NC se excluye — nunca resta a ciegas. Único caso real hoy: folios 63 y 71 (ambos sin tag,
 *     sin referencia).
 */
export const construirExclusionNcEmitidas = (facturasEmitidas = []) => {
  const { facturasAnuladas, notasCredito } = construirExclusionNcAnulaError(facturasEmitidas);
  const excluidas = new Set(facturasAnuladas);
  notasCredito.forEach((id) => excluidas.add(id));
  const ajusteComercial = []; // { ncId, refId, montoNeto, fecha_emision (de la NC) }
  (facturasEmitidas || []).forEach((f) => {
    if (!esNotaCredito(f)) return;
    if (excluidas.has(Number(f.id))) return; // ya resuelta por la rama 1 (anula_error con ref).
    const refId = f.documento_referenciado_id;
    if (f.tipo_relacion_nc === "ajuste_comercial" && refId != null) {
      ajusteComercial.push({
        ncId: Number(f.id),
        refId: Number(refId),
        montoNeto: Number(f.monto_neto || f.monto_exento || 0),
        fecha_emision: f.fecha_emision,
      });
      return;
    }
    // Rama 2 (anula_error SIN ref) y rama 4 (sin clasificar): ambas se excluyen, sin caso real
    // de la rama 2 hoy — ver comentario de la función.
    excluidas.add(Number(f.id));
  });
  return { excluidas, ajusteComercial };
};

/**
 * PASO 5c (2026-10-07) — bloque REAL de una fila de vinculación. `venta_tipo` solo alcanza para
 * `sales`/`sales_contratos`/`sales_btl`/`negocios_carteleria_hw` (1 bloque fijo cada uno) — pero
 * `negocios_carteleria` agrupa DOS líneas de negocio distintas bajo el MISMO `venta_tipo`:
 * hardware OPEX (cuota mensual, Bloque B) y gestión (Bloque C). El bug original de PASO 5
 * mandaba TODA vinculación `negocios_carteleria` a C — folio 571 (sep 2026, venta 11) repartía
 * $294.329 (gestión, a C) + $100.562 (OPEX, debía ir a B) y los dos quedaban en C. El bloque de
 * ESTAS vinculaciones sale de su `linea_id` → `lineas_facturacion.tipo`, con la MISMA regla que
 * `matchTipo` de ConciliacionSII (`resolverBloqueLineaCarteleria`, 10.1 — fuente única).
 *
 * Si la vinculación no tiene `linea_id`, o su línea no tiene `tipo` reconocido: no se puede
 * asignar bloque — se IGNORA (no entra a ningún bloque), documentado, no silencioso
 * (`meta.sinLinea`/`meta.lineaSinTipo` en el registro de diagnóstico de abajo). 0 casos reales
 * medidos en Chile 2026 (ver doc de cierre, PARTE 1) — `diagnosticoCartelería` existe para que
 * un caso futuro se note (no para que pase inadvertido).
 */
const resolverBloqueVinculacion = (v, lineaIdATipo) => {
  if (v.venta_tipo !== "negocios_carteleria") return VENTA_TIPO_A_BLOQUE_ER[v.venta_tipo] || null;
  if (v.linea_id == null) return null;
  const tipoLinea = lineaIdATipo.get(Number(v.linea_id)) ?? null;
  return resolverBloqueLineaCarteleria(tipoLinea);
};

/**
 * Lista, sin asignar ningún registro, las vinculaciones de `negocios_carteleria` cuyo bloque NO
 * se pudo resolver (sin `linea_id`, o `linea_id` sin fila en `lineas_facturacion`, o tipo de
 * línea no reconocido por `BLOQUE_POR_TIPO_LINEA_CARTELERIA`) — punto 3 del encargo de PASO 5c:
 * "si alguna vinculación de cartelería no tiene linea_id o su línea no tiene tipo: PARAR y
 * listarla". Solo lectura, para medición/test — `registrosIngresoSII` las ignora en silencio
 * documentado (no hay bloque al que asignarles un registro).
 */
export const diagnosticoCarteleriaSinBloque = (facturasEmitidas, lineaIdATipo) => {
  const casos = [];
  (facturasEmitidas || []).forEach((f) => {
    (f.facturas_sii_vinculaciones || []).forEach((v) => {
      if (v.venta_tipo !== "negocios_carteleria") return;
      if (resolverBloqueVinculacion(v, lineaIdATipo) != null) return;
      casos.push({
        facturaId: f.id,
        folio: f.folio,
        ventaRefId: v.venta_ref_id,
        lineaId: v.linea_id ?? null,
        tipoLinea: v.linea_id != null ? (lineaIdATipo.get(Number(v.linea_id)) ?? null) : null,
      });
    });
  });
  return casos;
};

/**
 * Registros de ingreso 2026+ (Chile) de UN bloque, desde `facturas_sii_vinculaciones` — un
 * registro POR FILA de vinculación (nunca por factura, ver comentario de arriba sobre
 * multi-línea). `facturasEmitidas` trae el embed `facturas_sii_vinculaciones(venta_tipo,
 * venta_ref_id, venta_ref_index, monto_aplicado, linea_id)` (mismo shape que ya resuelve el mock
 * de los tests de línea base dorada — `useERv2Data.js:fetchVinculaciones`, ampliado con
 * `monto_aplicado` y los campos de NC para PASO 5). `lineaIdATipo` (Map `linea_id→tipo` de
 * `lineas_facturacion`) es lo que permite distinguir Bloque B de C dentro de `negocios_carteleria`
 * (PASO 5c, ver `resolverBloqueVinculacion`).
 *
 * NC `ajuste_comercial`: resta en el mes/bloque de la factura REFERENCIADA — se agrega un
 * registro negativo extra por cada NC de `ajusteComercial` cuyo `refId` cae en el bloque que
 * esta llamada está construyendo (se resuelve buscando el bloque real de la factura referenciada
 * entre sus propias vinculaciones; si la referenciada no tiene vinculación — no debería pasar,
 * una NC solo referencia facturas ya vinculadas — no se le puede asignar bloque y se ignora,
 * documentado, no silencioso: `meta.sinBloque`).
 */
export const registrosIngresoSII = ({ facturasEmitidas, pais, ano, bloque, lineaIdATipo }) => {
  const registros = [];
  const { excluidas, ajusteComercial } = construirExclusionNcEmitidas(facturasEmitidas);
  const facturaPorId = new Map((facturasEmitidas || []).map((f) => [Number(f.id), f]));
  const bloqueDeFactura = (f) => {
    const v = (f?.facturas_sii_vinculaciones || [])[0];
    return v ? resolverBloqueVinculacion(v, lineaIdATipo) : null;
  };

  (facturasEmitidas || []).forEach((f) => {
    if (esNotaCredito(f)) return; // las NC nunca tienen vinculación propia — ver función de arriba.
    if (f.estado_vinculacion !== "vinculada") return;
    if (excluidas.has(Number(f.id))) return;
    const fecha = String(f.fecha_emision).slice(0, 10);
    const fechaObj = parseLocalYMD(fecha);
    if (!fechaObj || fechaObj.getFullYear() !== ano) return;
    const mesIndice = fechaObj.getMonth();
    (f.facturas_sii_vinculaciones || []).forEach((v) => {
      if (resolverBloqueVinculacion(v, lineaIdATipo) !== bloque) return;
      const montoLocal = Number(v.monto_aplicado) || 0;
      if (!montoLocal) return;
      registros.push(
        crearRegistroFinancieroER({
          fecha,
          ano,
          mesIndice,
          montoLocal,
          bloque,
          concepto: "ingreso_sii",
          pais,
          meta: {
            facturaId: f.id,
            folio: f.folio,
            origen: v.venta_tipo,
            ventaRefId: v.venta_ref_id,
            ventaRefIndex: v.venta_ref_index,
          },
        })
      );
    });
  });

  // NC ajuste_comercial: resta en el mes de la NC, bloque de la factura referenciada.
  ajusteComercial.forEach((nc) => {
    const refFactura = facturaPorId.get(nc.refId);
    const bloqueRef = refFactura ? bloqueDeFactura(refFactura) : null;
    if (bloqueRef !== bloque) return; // ni es este bloque, ni se puede resolver (sin vinculación) — se ignora acá.
    const fecha = String(nc.fecha_emision).slice(0, 10);
    const fechaObj = parseLocalYMD(fecha);
    if (!fechaObj || fechaObj.getFullYear() !== ano) return;
    registros.push(
      crearRegistroFinancieroER({
        fecha,
        ano,
        mesIndice: fechaObj.getMonth(),
        montoLocal: -nc.montoNeto,
        bloque,
        concepto: "nc_ajuste_comercial",
        pais,
        meta: { ncId: nc.ncId, refId: nc.refId },
      })
    );
  });

  return registros;
};

/**
 * Set de `refKey` ("<venta_tipo>:<venta_ref_id>:<venta_ref_index|''>") de las ventas/contratos/
 * negocios QUE SÍ están vinculados en `ano` (Chile 2026+) — más el mapa refKey→`{fecha,
 * montoAplicado}` de la factura que las vinculó (`fecha_emision`, `monto_aplicado` de ESA fila
 * de vinculación). Excluye las facturas anuladas por NC `anula_error`/sin clasificar (mismo
 * `excluidas` que `registrosIngresoSII`) — una venta cuya factura fue anulada no es "vinculada"
 * para efectos de comisión, consistente con el ingreso.
 *
 * Usado para filtrar `oportunidades`/`contratos`/`salesBTL` ANTES de pasarlos a las funciones
 * de comisión existentes (`registrosCostoIntermediacion`/`registrosComisionEdificiosA`/
 * `registrosComisionComercialA`/`registrosComisionEdificiosBTL`) — point 4 del encargo: "ninguna
 * comisión en un mes sin su ingreso", en el mes de la FACTURA vinculada (no el de la venta).
 * Las funciones de comisión en sí NO se tocan (10.1): siguen leyendo `facturafecha`/`meses[].
 * facturafecha`/`importe`/`moneda_dolar` tal cual — acá se les da, para 2026+ Chile, un
 * `oportunidades`/`contratos` YA filtrado a lo vinculado, con `facturafecha` pisada por la fecha
 * real de la factura y (PASO 6c, "pesificar en la factura") el `importe`/`moneda_dolar` YA
 * pesificados con `monto_aplicado` cuando la venta es USD — el motor de comisión
 * (`oppEnLocal`/`importeLocalContrato`) ve `moneda_dolar:false` y no vuelve a convertir con TC.
 */
export const construirRefKeysVinculadosSII = ({ facturasEmitidas, ano }) => {
  const { excluidas } = construirExclusionNcEmitidas(facturasEmitidas);
  const refKeyAFecha = new Map();
  (facturasEmitidas || []).forEach((f) => {
    if (esNotaCredito(f)) return;
    if (f.estado_vinculacion !== "vinculada") return;
    if (excluidas.has(Number(f.id))) return;
    const fecha = String(f.fecha_emision).slice(0, 10);
    const fechaObj = parseLocalYMD(fecha);
    if (!fechaObj || fechaObj.getFullYear() !== ano) return;
    (f.facturas_sii_vinculaciones || []).forEach((v) => {
      const key = `${v.venta_tipo}:${v.venta_ref_id}:${v.venta_ref_index ?? ""}`;
      const montoAplicado = v.monto_aplicado != null ? Number(v.monto_aplicado) : null;
      refKeyAFecha.set(key, { fecha, montoAplicado });
    });
  });
  return refKeyAFecha;
};

/** Si `opp` es USD y hay `montoAplicado` real de su vinculación: pesifica (`importe`=
 * monto_aplicado, `moneda_dolar:false`) — PASO 6c, "todo se pesifica en la factura, las
 * comisiones se pagan en pesos, no se convierte al día de pago". Sin monto (vinculación de
 * cartelería/BTL que no trae `monto_aplicado`, o venta en moneda local) → sin cambios.
 * `monedaDolarOriginal:true` preserva el dato real para `oppParaTake` (PASO 7, ver su comentario)
 * — el take es un hecho independiente del importe de la venta, nunca se pesifica con ella. */
const pesificarSiAplica = (entidad, montoAplicado) =>
  entidad.moneda_dolar && montoAplicado != null
    ? { importe: montoAplicado, moneda_dolar: false, monedaDolarOriginal: true }
    : {};

/** `oportunidades` (Bloque A, `venta_tipo='sales'`) filtradas a vinculadas en `ano`, con
 * `facturafecha` pisada por la fecha real de la factura — insumo de las funciones de comisión
 * existentes, SIN tocarlas. */
export const oportunidadesVinculadasSII = (oportunidades, refKeyAFecha) =>
  (oportunidades || [])
    .filter((opp) => refKeyAFecha.has(`sales:${opp.id}:`))
    .map((opp) => {
      const { fecha, montoAplicado } = refKeyAFecha.get(`sales:${opp.id}:`);
      return { ...opp, facturafecha: fecha, ...pesificarSiAplica(opp, montoAplicado) };
    });

/** `contratos` (Bloque A, `venta_tipo='sales_contratos'`): cada `meses[idx]` se filtra/pisa por
 * separado — `venta_ref_index` de la vinculación es el índice del mes dentro de `meses[]`. El
 * monto pesificado queda en `montoAplicadoCLP` (no en `importe`, que sigue en USD crudo para
 * cualquier otro consumidor de `meses[]`) — lo consume `importeLocalContrato`, el único punto
 * donde los registros de comisión de contratos arman el CLP. */
export const contratosVinculadosSII = (contratos, refKeyAFecha) =>
  (contratos || []).map((c) => ({
    ...c,
    meses: (c.meses || [])
      .map((mesObj, idx) => {
        const key = `sales_contratos:${c.id}:${idx}`;
        if (!refKeyAFecha.has(key)) return null;
        const { fecha, montoAplicado } = refKeyAFecha.get(key);
        return {
          ...mesObj,
          facturafecha: fecha,
          montoAplicadoCLP: c.moneda_dolar && montoAplicado != null ? montoAplicado : null,
        };
      })
      .filter(Boolean),
  }));

/** Si `row` (BTL) es USD y hay `montoAplicado` real de su vinculación: pesifica el campo de BTL
 * (`total_investment`, NUNCA `importe` — BTL no usa ese campo) y preserva el USD original en
 * `montoOrigenUSD` (lo usa `rateComisionEdificiosBTL` para prorratear `building.importe`,
 * P2.117-1). Mismo mecanismo que `pesificarSiAplica` (ventas), campo distinto. */
const pesificarSiAplicaBTL = (row, montoAplicado) =>
  row.moneda_dolar && montoAplicado != null
    ? { total_investment: montoAplicado, montoOrigenUSD: Number(row.total_investment) || 0, moneda_dolar: false }
    : {};

/** `salesBTL` (Bloque D, `venta_tipo='sales_btl'`) — mismo patrón que `oportunidadesVinculadasSII`
 * (sin índice, igual que `sales`). 0 casos reales en Chile 2026 (única venta BTL de Chile es de
 * 2025) — sin caso real que validar hoy, implementado por completitud/consistencia. */
export const salesBTLVinculadasSII = (salesBTL, refKeyAFecha) =>
  (salesBTL || [])
    .filter((row) => refKeyAFecha.has(`sales_btl:${row.id}:`))
    .map((row) => {
      const { fecha, montoAplicado } = refKeyAFecha.get(`sales_btl:${row.id}:`);
      return { ...row, facturafecha: fecha, ...pesificarSiAplicaBTL(row, montoAplicado) };
    });

/**
 * FASE 5a (reingeniería del EBITDA, 2026-10-06) — fuente ÚNICA, para Chile, de las 8 filas-hoja
 * "atadas a la venta" de los Bloques A/B/C/D (ingreso de los 4 bloques + Costo intermediación +
 * Comisión edificios A + Comisión comercial A + Comisión edificios BTL): antes de esta fase el
 * `.local` de estas filas salía de los hooks viejos (`usePublicidadCalcs`/`useContratosCalcs`/
 * `useHardwareCalcs`/`useGestionCalcs`/`useBTLCalcs`) y el `.usd` salía de estas MISMAS funciones
 * de registros (Criterio 1, `index.js`/`chileLeavesUSD`) — dos caminos independientes que PASO 5
 * (3.9) divergió en silencio al cambiar solo el `.usd` a facturas SII vinculadas para 2026+
 * (10.1). Esta función es el único lugar que construye los registros de estas 8 filas; `.local`
 * (`agregarAMesesLocal`) y `.usd` (`agregarAMesesUSD`+`convertirRegistrosAUSD`) son dos
 * agregaciones de los MISMOS registros — nunca pueden divergir entre sí.
 *
 * `usarSII` default `ano >= 2026` (la regla del encargo original); FASE 5a la pasa explícitamente
 * en `false` en sus 2 call sites (`index.js`) para no activar el cutover todavía — el número no
 * cambia un ápice, solo la fuente. FASE 5b retira ese `false` explícito: el cutover mueve
 * `.local` y `.usd` juntos, porque ambos ya comparten el mismo array de registros.
 */
export const construirVentaLeavesChile = (ano, inputs) => {
  const {
    oportunidadesParaPComun,
    contratosParaPComun,
    salesBTLParaPComun,
    negociosParaPComun,
    getDolarValueParaPComun,
    dolarFallbackPaisParaPComun,
    clientesParaPComun,
    intermediariosParaPComun,
    edificiosParaPComun,
    pctMapParaPComun,
    snapMapParaPComun,
    costosMapParaPComun,
    getUFValue,
    convertirAMonedaLocal,
    dolarMensualPeru,
    dolarFallback,
    facturasEmitidasChile,
    lineaIdATipo,
    usarSII = ano >= 2026,
  } = inputs;
  const refKeyAFecha = usarSII ? construirRefKeysVinculadosSII({ facturasEmitidas: facturasEmitidasChile, ano }) : null;
  const pComun = {
    oportunidades: usarSII
      ? oportunidadesVinculadasSII(oportunidadesParaPComun, refKeyAFecha)
      : oportunidadesParaPComun,
    contratos: usarSII ? contratosVinculadosSII(contratosParaPComun, refKeyAFecha) : contratosParaPComun,
    salesBTL: usarSII ? salesBTLVinculadasSII(salesBTLParaPComun, refKeyAFecha) : salesBTLParaPComun,
    negocios: negociosParaPComun,
    pais: "Chile",
    ano,
    getDolarValue: getDolarValueParaPComun,
    dolarFallbackPais: dolarFallbackPaisParaPComun,
    clientes: clientesParaPComun,
    intermediarios: intermediariosParaPComun,
    edificios: edificiosParaPComun,
    pctMap: pctMapParaPComun,
    snapMap: snapMapParaPComun,
    costosMap: costosMapParaPComun,
    getUFValue,
    convertirAMonedaLocal,
    dolarMensualPeru,
    dolarFallback,
  };
  const registrosIngresoBloque = (bloque, funcionVieja) =>
    usarSII
      ? registrosIngresoSII({ facturasEmitidas: facturasEmitidasChile, pais: "Chile", ano, bloque, lineaIdATipo })
      : funcionVieja(pComun);
  const usdYLocal = (registros) => ({
    registros,
    usd: agregarAMesesUSD(convertirRegistrosAUSD(registros), { pais: "Chile", ano }),
    local: agregarAMesesLocal(registros, { pais: "Chile", ano }),
  });
  const porLabel = {
    "A - Publicidad": usdYLocal(registrosIngresoBloque("A", registrosIngresoA)),
    "A - Publicidad / Costo intermediación": usdYLocal(registrosCostoIntermediacion(pComun)),
    "A - Publicidad / Comisión edificios": usdYLocal(registrosComisionEdificiosA(pComun)),
    "A - Publicidad / Comisión comercial": usdYLocal(registrosComisionComercialA(pComun)),
    "B - Hardware": usdYLocal(registrosIngresoBloque("B", registrosHardware)),
    "C - Gestión & Contenidos": usdYLocal(registrosIngresoBloque("C", registrosGestion)),
    "D - BTL & otros": usdYLocal(registrosIngresoBloque("D", registrosBTL)),
    "D - BTL & otros / Comisión edificios": usdYLocal(registrosComisionEdificiosBTL(pComun)),
  };
  const usd = {};
  const local = {};
  const registrosPorLabel = {};
  Object.entries(porLabel).forEach(([label, v]) => {
    usd[label] = v.usd;
    local[label] = v.local;
    registrosPorLabel[label] = v.registros;
  });
  // PASO 6 (2026-10-07) — `registros` expone, para cada una de las 8 filas, los MISMOS
  // RegistroFinancieroER que ya suman `.local`/`.usd` (10.1) — fuente de los 6 modales de
  // detalle de Chile (`index.js`), en vez de las listas paralelas de los hooks viejos
  // (`usePublicidadCalcs`/`useContratosCalcs`). "Suma de filas del modal == celda" se cumple
  // por construcción: son el MISMO array, agregado dos veces (una para el total, otra para el
  // detalle), nunca dos cálculos independientes.
  return { usd, local, registros: registrosPorLabel };
};
